/**
 * Web Page Viewed Event Component
 *
 * Renders a page view event on the session timeline.
 * Header pattern: "<ActorName> captured a page view of **[page title or URL]** on this web visit"
 * Associated pattern: "<ActorName> captured a page view of **[page title or URL]** on <CrmObjectBadge>"
 */

import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';

import { EntityBadge } from '@/lib/crm/components/badges/EntityBadge';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';

import {
  ActorName,
  bold,
  InlineText,
  TimelineEvent,
} from '../components/shared';
import { useViewStateLookups } from '../context/TimelineLookupsContext';
import type { WebPageViewedPayload } from '../types';
import { useEventFormatters } from '../useEventFormatters';
import type { EventComponentProps } from './types';

export function WebPageViewedEvent({ event, entityType }: EventComponentProps) {
  const { getActorInfo, getObjectLabel } = useEventFormatters();
  const { lookups } = useViewStateLookups();
  const actor = getActorInfo(event);
  const openSlideout = useOpenSlideoutOptional();
  const payload = event.payload as WebPageViewedPayload | undefined;
  const displayText = payload?.page_title || payload?.page_url || 'a page';

  // Check if this is an associated event (from a different entity)
  const isAssociatedEvent = 'association_type_name' in event;
  const associatedObject = isAssociatedEvent
    ? lookups.entities[event.entity_id]
    : undefined;

  const handleObjectClick = useCallback(() => {
    if (associatedObject) {
      openSlideout?.(associatedObject.entity_id);
    }
  }, [openSlideout, associatedObject]);

  const objectBadgeState = useMemo(
    () =>
      associatedObject
        ? {
            kind: 'data' as const,
            data: {
              id: associatedObject.entity_id,
              entityType: associatedObject.entity_type_name,
              displayName: associatedObject.ahoy_display_name ?? 'Unknown',
            },
          }
        : null,
    [associatedObject]
  );

  const entityLabel = entityType
    .replace(/^ahoy_/, '')
    .replace(/_entity$/, '')
    .replace(/_/g, ' ');

  const objectDisplayText = isAssociatedEvent
    ? getObjectLabel(event.entity_id)
    : `this ${entityLabel}`;

  let objectDisplay: ReactNode;
  if (isAssociatedEvent && objectBadgeState) {
    objectDisplay = (
      <EntityBadge
        state={objectBadgeState}
        className='align-middle'
        onClick={openSlideout ? handleObjectClick : undefined}
      />
    );
  } else if (isAssociatedEvent) {
    objectDisplay = bold(objectDisplayText);
  } else {
    objectDisplay = objectDisplayText;
  }

  const ariaLabel = `${actor.name} captured a page view of ${displayText} on ${objectDisplayText}`;

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Content
        eventId={event.event_id}
        header={
          <InlineText>
            <ActorName actor={actor} /> captured a page view of{' '}
            {bold(displayText)} on {objectDisplay}
          </InlineText>
        }
        timestamp={event.occurred_at}
      />
    </TimelineEvent>
  );
}
